import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { 
  Upload, X, Sparkles, Loader2, Image as ImageIcon, 
  GripVertical, AlertCircle, CheckCircle 
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ImageUploader({ 
  images = [], 
  onImagesChange, 
  maxImages = 5,
  showAIEnhance = true 
}) {
  const [uploading, setUploading] = useState(false);
  const [enhancing, setEnhancing] = useState(null); // Index of image being enhanced
  const [aiPrompt, setAiPrompt] = useState('');
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Handle file drop
  const onDrop = useCallback(async (acceptedFiles) => {
    if (images.length + acceptedFiles.length > maxImages) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }

    setUploading(true);
    const newImages = [...images];

    for (const file of acceptedFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API_URL}/api/upload-image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        newImages.push(response.data.url);
        toast.success('Image uploaded & optimized to 1000x1000');
      } catch (error) {
        console.error('Upload failed:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    onImagesChange(newImages);
    setUploading(false);
  }, [images, maxImages, onImagesChange]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.webp']
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    disabled: uploading || images.length >= maxImages
  });

  // Remove image
  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onImagesChange(newImages);
  };

  // Handle drag start
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handle drag over
  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
  };

  // Handle drop for reordering
  const handleDropReorder = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newImages = [...images];
    const draggedImage = newImages[draggedIndex];
    newImages.splice(draggedIndex, 1);
    newImages.splice(dropIndex, 0, draggedImage);
    
    onImagesChange(newImages);
    setDraggedIndex(null);
    toast.success('Image order updated');
  };

  // AI Enhancement
  const openAIEnhanceModal = (index) => {
    setSelectedImageIndex(index);
    setAiPrompt('Transform this into a professional product photography with clean white background, perfect studio lighting, and premium e-commerce presentation');
    setShowPromptModal(true);
  };

  const enhanceWithAI = async () => {
    if (selectedImageIndex === null) return;
    
    setShowPromptModal(false);
    setEnhancing(selectedImageIndex);

    try {
      // First, fetch the current image
      const currentImageUrl = images[selectedImageIndex];
      const fullUrl = currentImageUrl.startsWith('http') 
        ? currentImageUrl 
        : `${API_URL}${currentImageUrl}`;

      // Fetch the image as blob
      const imageResponse = await fetch(fullUrl);
      const imageBlob = await imageResponse.blob();

      // Create form data with image and prompt
      const formData = new FormData();
      formData.append('file', imageBlob, 'product.jpg');
      formData.append('prompt', aiPrompt);

      const response = await axios.post(`${API_URL}/api/enhance-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000 // 2 minute timeout for AI processing
      });

      // Replace the image with enhanced version
      const newImages = [...images];
      newImages[selectedImageIndex] = response.data.url;
      onImagesChange(newImages);

      toast.success('Image enhanced with AI!');
    } catch (error) {
      console.error('AI enhancement failed:', error);
      toast.error('AI enhancement failed. Please try again.');
    } finally {
      setEnhancing(null);
      setSelectedImageIndex(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Image Size Guidelines */}
      <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
        <div className="text-sm">
          <span className="font-medium text-blue-800">Recommended: 1000x1000 pixels (square)</span>
          <span className="text-blue-600 ml-2">• Images auto-resize to standard size</span>
        </div>
      </div>

      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`
          relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
          ${isDragActive ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-green-400 hover:bg-gray-50'}
          ${uploading || images.length >= maxImages ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input {...getInputProps()} data-testid="image-dropzone-input" />
        
        {uploading ? (
          <div className="flex flex-col items-center">
            <Loader2 className="w-12 h-12 text-green-600 animate-spin mb-3" />
            <p className="text-gray-600">Uploading & optimizing...</p>
          </div>
        ) : isDragActive ? (
          <div className="flex flex-col items-center">
            <Upload className="w-12 h-12 text-green-600 mb-3" />
            <p className="text-green-700 font-medium">Drop images here!</p>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <ImageIcon className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-700 font-medium mb-1">
              Drag & drop product images here
            </p>
            <p className="text-gray-500 text-sm mb-3">
              or click to browse (JPG, PNG, WebP • Max 10MB)
            </p>
            <Button type="button" variant="outline" size="sm" disabled={images.length >= maxImages}>
              <Upload className="w-4 h-4 mr-2" />
              Select Images
            </Button>
            <p className="text-xs text-gray-400 mt-3">
              {images.length}/{maxImages} images uploaded
            </p>
          </div>
        )}
      </div>

      {/* Image Preview Grid */}
      {images.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">
              Uploaded Images (drag to reorder)
            </label>
            <span className="text-xs text-gray-500">First image = Main product image</span>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {images.map((image, index) => (
              <Card
                key={index}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDropReorder(e, index)}
                className={`
                  relative group overflow-hidden cursor-move
                  ${draggedIndex === index ? 'opacity-50 scale-95' : ''}
                  ${index === 0 ? 'ring-2 ring-green-500' : ''}
                `}
                data-testid={`product-image-${index}`}
              >
                {/* Main badge for first image */}
                {index === 0 && (
                  <div className="absolute top-2 left-2 z-10 px-2 py-1 bg-green-600 text-white text-xs font-medium rounded">
                    Main
                  </div>
                )}
                
                {/* Drag handle */}
                <div className="absolute top-2 right-2 z-10 p-1 bg-white/80 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                  <GripVertical className="w-4 h-4 text-gray-500" />
                </div>

                {/* Image */}
                <div className="aspect-square bg-gray-100">
                  {enhancing === index ? (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-purple-50">
                      <Loader2 className="w-8 h-8 text-purple-600 animate-spin mb-2" />
                      <span className="text-xs text-purple-700">AI Enhancing...</span>
                    </div>
                  ) : (
                    <img
                      src={image.startsWith('http') ? image : `${API_URL}${image}`}
                      alt={`Product ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>

                {/* Action buttons */}
                <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="flex justify-center gap-2">
                    {showAIEnhance && (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        className="h-7 text-xs bg-purple-600 hover:bg-purple-700 text-white"
                        onClick={() => openAIEnhanceModal(index)}
                        disabled={enhancing !== null}
                        data-testid={`ai-enhance-btn-${index}`}
                      >
                        <Sparkles className="w-3 h-3 mr-1" />
                        AI Enhance
                      </Button>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      className="h-7 text-xs"
                      onClick={() => removeImage(index)}
                      disabled={enhancing !== null}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* AI Prompt Modal */}
      {showPromptModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <Card className="w-full max-w-lg mx-4 p-6 bg-white shadow-2xl border-0">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">AI Image Enhancement</h3>
                <p className="text-sm text-gray-500">Describe how you want your product to look</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enhancement Prompt
                </label>
                <textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                  rows={4}
                  placeholder="Describe how you want the product to look..."
                  data-testid="ai-prompt-input"
                />
              </div>

              {/* Quick prompts */}
              <div className="space-y-2">
                <label className="block text-xs font-medium text-gray-500">Quick prompts:</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    'Professional product photo with white background',
                    'Premium e-commerce style with soft shadows',
                    'Studio lighting with clean presentation',
                    'Lifestyle product shot with warm tones'
                  ].map((prompt, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setAiPrompt(prompt)}
                      className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-purple-100 rounded-full text-gray-700 hover:text-purple-700 transition-colors border border-gray-200"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowPromptModal(false)}
                  className="border-gray-300"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={enhanceWithAI}
                  data-testid="confirm-ai-enhance-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Enhance with AI
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
